/**
 * Visit Frequency Overlay Component
 */

import { useState } from "react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { Progress } from "../ui/progress";
import { Calendar } from "lucide-react";

export function VisitFrequencyOverlay({ patientId, patientName, onSave, onClose }) {
  const [visitData, setVisitData] = useState({
    benefit_period: "01/15/2026 - 04/14/2026",
    days_in_period: 90,
    days_elapsed: 11,
    disciplines: [
      {
        name: "Skilled Nursing (SN)",
        code: "SN",
        ordered_frequency: "2-3x/week",
        ordered_visits: 36,
        completed_visits: 5,
        scheduled_visits: 3,
        remaining_visits: 28,
        last_visit: "01/24/2026",
        next_visit: "01/27/2026",
        compliance: 100
      },
      {
        name: "Medical Social Worker (MSW)",
        code: "MSW",
        ordered_frequency: "1x/week PRN",
        ordered_visits: 12,
        completed_visits: 2,
        scheduled_visits: 1,
        remaining_visits: 9,
        last_visit: "01/22/2026",
        next_visit: "01/29/2026",
        compliance: 100
      },
      {
        name: "Chaplain",
        code: "Chaplain",
        ordered_frequency: "1x/week PRN",
        ordered_visits: 12,
        completed_visits: 1,
        scheduled_visits: 0,
        remaining_visits: 11,
        last_visit: "01/18/2026",
        next_visit: "Pending",
        compliance: 75
      },
      {
        name: "Home Health Aide (HHA)",
        code: "HHA",
        ordered_frequency: "3x/week",
        ordered_visits: 39,
        completed_visits: 4,
        scheduled_visits: 3,
        remaining_visits: 32,
        last_visit: "01/25/2026",
        next_visit: "01/27/2026",
        compliance: 100
      },
      {
        name: "Volunteer",
        code: "Vol",
        ordered_frequency: "1x/week PRN",
        ordered_visits: 12,
        completed_visits: 0,
        scheduled_visits: 1,
        remaining_visits: 11,
        last_visit: "N/A",
        next_visit: "01/30/2026",
        compliance: 0
      }
    ]
  });

  const getComplianceColor = (compliance) => {
    if (compliance >= 90) return "bg-green-500";
    if (compliance >= 75) return "bg-amber-500";
    return "bg-red-500";
  };

  const totalOrdered = visitData.disciplines.reduce((sum, d) => sum + d.ordered_visits, 0);
  const totalCompleted = visitData.disciplines.reduce((sum, d) => sum + d.completed_visits, 0);
  const overallCompliance = Math.round((totalCompleted / (totalOrdered * (visitData.days_elapsed / visitData.days_in_period))) * 100);

  return (
    <div className="space-y-4 p-4" data-testid="visit-frequency-overlay">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-blue-400" />
          <h3 className="font-semibold text-white">Discipline Visit Frequency</h3>
          <Badge variant="outline" className="border-slate-600 text-slate-300">{patientName}</Badge>
        </div>
      </div>

      {/* Period Summary */}
      <div className="p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-blue-300">Current Benefit Period</span>
          <Badge className="bg-blue-600">{visitData.benefit_period}</Badge>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-400">Day {visitData.days_elapsed} of {visitData.days_in_period}</span>
          <Progress value={(visitData.days_elapsed / visitData.days_in_period) * 100} className="w-32 h-2" />
        </div>
      </div>

      {/* Overall Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="p-3 bg-slate-800/50 rounded-lg text-center border border-slate-700">
          <p className="text-2xl font-bold text-teal-400">{totalCompleted}</p>
          <p className="text-xs text-slate-400">Visits Completed</p>
        </div>
        <div className="p-3 bg-slate-800/50 rounded-lg text-center border border-slate-700">
          <p className="text-2xl font-bold text-blue-400">{totalOrdered}</p>
          <p className="text-xs text-slate-400">Total Ordered</p>
        </div>
        <div className="p-3 bg-slate-800/50 rounded-lg text-center border border-slate-700">
          <p className={`text-2xl font-bold ${overallCompliance >= 90 ? "text-green-400" : overallCompliance >= 75 ? "text-amber-400" : "text-red-400"}`}>
            {Math.min(100, overallCompliance)}%
          </p>
          <p className="text-xs text-slate-400">On Track</p>
        </div>
      </div>

      {/* Discipline Breakdown */}
      <ScrollArea className="h-[350px] pr-2">
        <div className="space-y-3">
          {visitData.disciplines.map((disc, idx) => (
            <div key={idx} className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Badge className="bg-slate-700">{disc.code}</Badge>
                  <span className="font-medium text-white">{disc.name}</span>
                </div>
                <Badge className={getComplianceColor(disc.compliance)}>
                  {disc.compliance}% On Track
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                <div>
                  <p className="text-xs text-slate-400">Ordered Frequency</p>
                  <p className="font-medium text-slate-200">{disc.ordered_frequency}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Visits This Period</p>
                  <p className="font-medium text-slate-200">{disc.completed_visits} / {disc.ordered_visits}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Last Visit</p>
                  <p className="font-medium text-slate-200">{disc.last_visit}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Next Visit</p>
                  <p className={`font-medium ${disc.next_visit === "Pending" ? "text-amber-400" : "text-slate-200"}`}>{disc.next_visit}</p>
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex justify-between text-xs text-slate-400">
                  <span>Visit Progress</span>
                  <span>{disc.completed_visits} completed, {disc.scheduled_visits} scheduled, {disc.remaining_visits} remaining</span>
                </div>
                <Progress value={(disc.completed_visits / disc.ordered_visits) * 100} className="h-2" />
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300 hover:bg-slate-800">Close</Button>
        <Button className="bg-blue-600 hover:bg-blue-700">
          <Calendar className="w-4 h-4 mr-1" />Schedule Visit
        </Button>
      </div>
    </div>
  );
}

export default VisitFrequencyOverlay;
